import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { initSocket, disconnectSocket } from '../utils/socket';

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());
  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('nexus_token');
    if (!token) return;

    const socket = initSocket(token);
    socketRef.current = socket;

    const onConnect = () => {
      setConnected(true);
      // Immediately mark self online and ask for a fresh presence snapshot
      setOnlineUserIds(prev => new Set(prev).add(user._id || user.id));
    };
    const onDisconnect = () => setConnected(false);

    const onUserStatus = ({ userId, isOnline: online }) => {
      setOnlineUserIds(prev => {
        const next = new Set(prev);
        if (online) next.add(userId); else next.delete(userId);
        return next;
      });
    };

    // Snapshot of everyone already online, sent right after connecting.
    // Fixes asymmetric presence: without this, a user who connects AFTER
    // someone else was already online would never learn that person is online.
    const onPresenceInit = ({ onlineUsers }) => {
      setOnlineUserIds(new Set(onlineUsers || []));
    };

    const onDmNotification = (data) => {
      setNotifications(prev => [{ ...data, id:`notif_${Date.now()}_${Math.random()}` }, ...prev].slice(0,50));
    };

    socket.on('connect',        onConnect);
    socket.on('disconnect',     onDisconnect);
    socket.on('user:status',    onUserStatus);
    socket.on('dm:notification',onDmNotification);
    socket.on('presence:init',   onPresenceInit);

    if (socket.connected) onConnect();

    return () => {
      socket.off('connect',        onConnect);
      socket.off('disconnect',     onDisconnect);
      socket.off('user:status',    onUserStatus);
      socket.off('dm:notification',onDmNotification);
      socket.off('presence:init',   onPresenceInit);
    };
  }, [user]);

  // Cleanup socket entirely on logout
  useEffect(() => {
    if (!user) {
      disconnectSocket();
      setOnlineUserIds(new Set());
      setConnected(false);
    }
  }, [user]);

  const isOnline = useCallback((userId) => {
    if (!userId) return false;
    if (userId === (user?._id||user?.id)) return connected; // your own status = your own connection
    return onlineUserIds.has(userId?.toString());
  }, [onlineUserIds, connected, user]);

  const clearNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearNotificationsFor = useCallback((userId) => {
    setNotifications(prev => prev.filter(n => n.senderId !== userId));
  }, []);

  return (
    <SocketContext.Provider value={{
      socket: socketRef.current,
      connected,
      isOnline,
      notifications,
      clearNotification,
      clearNotificationsFor,
    }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket must be used within SocketProvider');
  return ctx;
};